/**
 * HouseholdNameForm Component
 *
 * Form for renaming a household. Only displayed to household owners.
 * Validates name is not empty and saves via API.
 *
 * WP-HOUSEHOLD-2: Household Invitation Onboarding
 */

import { useState, useCallback, useEffect } from 'react';
import { Input, Button } from '@/components/ui';
import { useUpdateHousehold } from '@/hooks/useHouseholds';
import { toast } from '@/components/ui/Toaster';

export interface HouseholdNameFormProps {
  currentName: string;
  tenantId: string;
}

export default function HouseholdNameForm({
  currentName,
  tenantId,
}: HouseholdNameFormProps) {
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  const updateHousehold = useUpdateHousehold();

  // Sync with prop changes (e.g., after successful save)
  useEffect(() => {
    setName(currentName);
    setError(null);
  }, [currentName]);

  // Check if name has changed from current
  const hasChanges = name.trim() !== currentName;

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setName(newValue);
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  }, [error]);

  // Handle form submission
  const handleSave = useCallback(async () => {
    // Validate
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Household name cannot be empty');
      return;
    }

    if (trimmedName.length > 100) {
      setError('Household name must be 100 characters or less');
      return;
    }

    try {
      await updateHousehold.mutateAsync({
        tenantId,
        name: trimmedName,
      });
      toast.success('Household name updated');
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update household name';
      setError(message);
      toast.error(message);
    }
  }, [name, tenantId, updateHousehold]);

  // Handle form submission on Enter key
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && hasChanges && !updateHousehold.isPending) {
        e.preventDefault();
        handleSave();
      }
    },
    [hasChanges, updateHousehold.isPending, handleSave]
  );

  return (
    <div className="flex gap-3 items-start">
      <div className="flex-1">
        <Input
          id="household-name"
          label=""
          value={name}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter household name"
          maxLength={100}
          error={error || undefined}
          disabled={updateHousehold.isPending}
          aria-label="Household name"
        />
      </div>
      <Button
        variant="primary"
        onClick={handleSave}
        disabled={!hasChanges || updateHousehold.isPending}
        isLoading={updateHousehold.isPending}
        className="mt-0"
      >
        Save
      </Button>
    </div>
  );
}
